import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useVisibilityRefetch } from './useVisibilityRefetch';

export interface SparkQuestion {
  id: string;
  question: string;
  type: string;
  age_min_months: number;
  age_max_months: number;
  active: boolean;
}

export function useSparkQuestion(ageMonths: number) {
  const [question, setQuestion] = useState<SparkQuestion | null>(null);

  const fetchQuestion = useCallback(() => {
    if (!ageMonths) return;

    const shownKey = `shown_sparks_${Math.floor(ageMonths)}`;
    const shown: string[] = JSON.parse(localStorage.getItem(shownKey) ?? '[]');

    const notInClause =
      shown.length > 0
        ? shown.join(',')
        : '00000000-0000-0000-0000-000000000000';

    void supabase
      .from('spark_questions')
      .select('id, question, type, age_min_months, age_max_months, active')
      .eq('active', true)
      .lte('age_min_months', ageMonths)
      .gte('age_max_months', ageMonths)
      .not('id', 'in', `(${notInClause})`)
      .limit(20)
      .then(({ data }) => {
        if (!data?.length) {
          // All questions shown — reset and start over
          localStorage.removeItem(shownKey);
          setQuestion(null);
          return;
        }
        const random = data[Math.floor(Math.random() * data.length)] as SparkQuestion;
        setQuestion(random);
        localStorage.setItem(shownKey, JSON.stringify([...shown, random.id]));
      });
  }, [ageMonths]);

  useEffect(() => {
    fetchQuestion();
  }, [fetchQuestion]);

  // Refetch when the tab regains focus so newly-seeded sparks show up.
  useVisibilityRefetch(fetchQuestion);

  return { question, refresh: fetchQuestion };
}

/** Calculates age in months from a birthdate string (YYYY-MM-DD) */
export function ageInMonths(birthdate: string | null | undefined): number {
  if (!birthdate) return 36; // default to 3 years if unknown
  const birth = new Date(birthdate);
  const now = new Date();
  return (
    (now.getFullYear() - birth.getFullYear()) * 12 +
    (now.getMonth() - birth.getMonth())
  );
}
